import React, { useState } from "react";
import {
  PageHeader,
  Icons,
  IconButton,
  Button,
  DataTable,
  useClientTable,
  Text,
  Menu,
} from "@pushpress/shared-components";
import { useToggle } from "../../../hooks/state";
import { openWindow } from "../../../utils/browserUtils";
import ColorSquare from "../../../components/common/ColorSquare";
import PageContainer from "../../../components/common/PageContainer/PageContainer";
import { useAppointmentTypesQuery } from "../../../apis/appointmentApi.hooks";
import { AppointmentType } from "../Appointments.types";
import AppointmentTypeDrawer from "../AppointmentTypeDrawer";
import DeleteModal from "./AppointmentTypeDeleteModal";
import InfoModal from "./AppointmentTypeInfoModal";
import { EmptyState } from "@pushpress/pushpress-ui";
import AppointmentIcon from "../../../components/icons/AppointmentIcon";

const columns = [
  {
    key: "name",
    header: "Name",
    width: "400px",
    widthMobile: "auto",
    isSortable: true,
    cell: (row: AppointmentType) => (
      <>
        <ColorSquare color={row.color} className="mr-2" />
        <Text variant="body-lg" component="span">
          {row.name}
        </Text>
      </>
    ),
  },
  {
    key: "duration",
    header: "Duration",
    isSortable: true,
    hideOnMobile: true,
    cell: (row: AppointmentType) => (
      <Text variant="body-lg">{`${row.duration / 60} min`}</Text>
    ),
  },
];

function AppointmentTypeList() {
  const [duplicate, duplicateActions] = useToggle(false);
  const [editData, setEditData] = useState<AppointmentType>();
  const [drawerVisible, drawerActions] = useToggle(false);
  const [deleteVisible, deleteActions] = useToggle(false);
  const [infoVisible, infoActions] = useToggle(false);

  const appointmentTypesQuery = useAppointmentTypesQuery();

  const { sort, data, handleSort } = useClientTable({
    data: appointmentTypesQuery?.data?.data || [],
    columns,
    defaultSort: { key: "name", order: "asc" },
  });

  const handleInfoClick = (data: AppointmentType) => {
    setEditData(data);
    infoActions.on();
  };

  const handleEditClick = (data: AppointmentType) => {
    setEditData(data);
    duplicateActions.off();
    drawerActions.on();
  };

  const handleDuplicateClick = (data: AppointmentType) => {
    setEditData(data);
    duplicateActions.on();
    drawerActions.on();
  };

  const handleDeleteClick = (data: AppointmentType) => {
    setEditData(data);
    deleteActions.on();
  };

  const handleAddClick = () => {
    setEditData(undefined);
    duplicateActions.off();
    drawerActions.on();
  };

  const handleHelpClick = () => {
    openWindow(
      "https://help.pushpress.com/en/articles/6310720-core-appointment-types-details-staff-availability-notifications",
    );
  };

  const handleDrawerClose = () => {
    setEditData(undefined);
    drawerActions.off();
  };

  const handleSuccess = () => {
    setEditData(undefined);
    drawerActions.off();
    appointmentTypesQuery.refetch();
  };

  const getRowId = (row: AppointmentType) => {
    return row.uuid;
  };

  const renderRowMenu = (row: AppointmentType, closeMenu: VoidFunction) => {
    return (
      <Menu>
        <Menu.Item
          label="Info"
          action={() => {
            handleInfoClick(row);
            closeMenu();
          }}
        ></Menu.Item>
        <Menu.Item
          label="Edit"
          action={() => {
            handleEditClick(row);
            closeMenu();
          }}
        ></Menu.Item>
        <Menu.Item
          label="Duplicate"
          action={() => {
            handleDuplicateClick(row);
            closeMenu();
          }}
        ></Menu.Item>
        <Menu.Item
          label="Delete"
          action={() => {
            handleDeleteClick(row);
            closeMenu();
          }}
        ></Menu.Item>
      </Menu>
    );
  };

  const emptyState = (
    <EmptyState
      icon={<AppointmentIcon />}
      title="No appointment types found"
      description="Create an appointment type to define the details of your classes, appointments, and client assignments."
      primaryActionLabel="Add Appointment Type"
      primaryActionVariant="dark"
      primaryActionOnClick={handleAddClick}
      learnMoreHref="https://help.pushpress.com/en/articles/6310720-core-appointment-types-details-staff-availability-notifications"
      learnMoreLabel="Learn more"
    />
  );

  return (
    <div className="tw-flex tw-flex-col md:tw-px-8 md:tw-pt-4 tw-w-full tw-mx-auto tw-max-w-7xl">
      <PageContainer>
        <PageHeader title="Appointment Types">
          <PageHeader.ActionMenu>
            <IconButton
              icon={Icons.Help}
              tooltip="Learn more"
              onClick={handleHelpClick}
            />
            <Button
              text="Type"
              size="large"
              iconLocation="left"
              hideTextOnMobile
              icon={Icons.Plus}
              iconBold
              onClick={handleAddClick}
            />
          </PageHeader.ActionMenu>
        </PageHeader>
        <DataTable
          data={data}
          columns={columns}
          sort={sort}
          loading={appointmentTypesQuery.isLoading}
          getRowId={getRowId}
          onSort={handleSort}
          renderRowMenu={renderRowMenu}
          customEmptyState={emptyState}
        />
      </PageContainer>
      <AppointmentTypeDrawer
        visible={drawerVisible}
        duplicate={duplicate}
        appointmentTypeUuid={editData?.uuid}
        onClose={handleDrawerClose}
        onSuccess={handleSuccess}
      />
      <InfoModal
        visible={infoVisible}
        appointmentTypeUuid={editData?.uuid}
        onClose={infoActions.off}
      />
      <DeleteModal
        visible={deleteVisible}
        appointmentTypeUuid={editData?.uuid}
        onClose={deleteActions.off}
        onSuccess={handleSuccess}
      />
    </div>
  );
}

export default AppointmentTypeList;
